import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useClientStore } from '@shared/stores/client.js'
import { useClosetStore } from '@shared/stores/closet.js'
import { useSubscriptionStore } from '@/stores/subscribe.js'
import { useAccountStore } from '@/stores/account.js'
import { formatPrice, toLocaleDateString } from '@/utils/stringParsing.js'
import { useCancelReason } from './cancelReason.js'

export function usePauseCancel() {
  const clientStore = useClientStore()
  const closetStore = useClosetStore()
  const subscribeStore = useSubscriptionStore()
  const accountStore = useAccountStore()
  const { benefitsValue, availableSwaps, paidThrough, clientIsPrepay, bonusItems, membershipDetails, membershipStatus } = storeToRefs(clientStore)
  const { getBilling } = clientStore
  const { deliveredItems, purchaseAllItemsDiscountPrice } = storeToRefs(closetStore)
  const { cancelReasons } = storeToRefs(subscribeStore)
  const { getCancelReasons } = subscribeStore  
  const { itemPromotions } = storeToRefs(accountStore)
  const { getItemPromotions } = accountStore

  const hasItemsAtHome = computed(() => deliveredItems.value.length > 0)
  const hasBonusItems = computed(() => bonusItems.value.length > 0)
  const hasItemPromotions = computed(() => itemPromotions.value?.available?.length > 0) // accounts for item promotions and item sale promotions
  
  const couponCashValue = computed(() => {
    if (!hasItemPromotions.value) return 0
    const validPromotions = itemPromotions.value?.available?.filter(promo => {
      if (!promo.expiration) return true
      return new Date(promo.expiration) >= new Date()
    }) || []
    let totalCashValue = 0
    validPromotions.forEach(promo => {
      if (promo.amountOff) {
        totalCashValue += promo.amountOff
      }
    })
    return totalCashValue
  })
  
  const totalBenefitsValue = computed(() => {
    return (benefitsValue.value || 0) + couponCashValue.value
  })
  
  const availablePerks = computed(() => {
    const perks = []
    if (availableSwaps.value > 0) {
      const swapText = availableSwaps.value === 1 ? 'swap' : 'swaps'
      perks.push(`${availableSwaps.value} free ${swapText}`)
    }
    if (hasItemPromotions.value) {
      const validPromotions = itemPromotions.value?.available?.filter(promo => {
        if (!promo.expiration) return true
        return new Date(promo.expiration) >= new Date()
      }) || []
      let totalCashValue = 0
      let cashPromotionCount = 0
      validPromotions.forEach(promo => {
        if (promo.amountOff) {
          totalCashValue += promo.amountOff
          cashPromotionCount++
        } else {
          perks.push(`a ${promo.percentOff}% off coupon`)
        }
      })
      if (totalCashValue > 0) {
        const cashValue = formatPrice(totalCashValue)
        const cashText = cashPromotionCount > 1 ? `${cashValue} in coupons` : `a ${cashValue} coupon`
        perks.push(cashText)
      }
    }
    if (hasBonusItems.value) {
      const bonusCount = bonusItems.value.length
      const itemText = bonusCount === 1 ? 'bonus item' : 'bonus items'
      perks.push(`${bonusCount} ${itemText}`)
    }
    return perks
  })
  const formattedPerksList = computed(() => {
    const perks = availablePerks.value
    if (perks.length === 0) return ''
    if (perks.length === 1) return perks[0]
    return perks.slice(0, -1).join(', ') + ' and ' + perks[perks.length - 1]
  })
  const benefitsText = computed(() => {
    const benefits = []
    if (hasItemsAtHome.value) {
      const itemCount = deliveredItems.value.length
      benefits.push({
        id: 'items-at-home',
        text: `${itemCount} ${itemCount === 1 ? 'item' : 'items'} at home you can purchase for ${purchaseAllItemsDiscountPrice.value}% off`
      })
    }
    if (totalBenefitsValue.value > 0 && availablePerks.value.length > 0) {
      const perksText = `Over ${formatPrice(totalBenefitsValue.value)} in perks including ${formattedPerksList.value}`
      benefits.push({
        id: 'perks',
        text: perksText
      })
    }
    return benefits
  })
  const pauseCancelText = computed(() => {
    if (availablePerks.value.length > 0) {
      return `Your account has <b> over ${formatPrice(totalBenefitsValue.value)} in remaining perks</b>, including ${formattedPerksList.value}`
    }
    return 'Pause your membership for a month instead of canceling. It\'s free, flexible, and you can pick up right where you left off.'
  })
  const pauseCancelFaqs = computed(() => [
    {
      id: 'what-does-pausing-mean',
      question: 'What does pausing my membership mean?',
      answer: 'Pausing your membership means that you will not be charged for a month of your Armoire membership, and you\'ll need to return any case items that are currently with you. Monthly charges will resume one month later.<br>Unlike cancelling, pausing your membership allows you to retain all of your benefits, including available shipment, bonus item, and unused referral credits.'
    },
    {
      id: 'what-do-i-need-to-do',
      question: 'Anything I need to do?',
      answer: 'Please return the items that you have with your prepaid shipping label. These items must be received and processed by Armoire within 30 days, otherwise you will not be able to extend your pause and your membership will be reinstated.'
    },
    {
      id: 'other-questions',
      question: 'Other questions?',
      answer: 'Chat with our customer support team to address any outstanding questions.',
      hasCustomContent: true
    }
  ])
  const pauseDates = computed(() => {
    if (!paidThrough.value || clientIsPrepay.value === null) {
      return { startDate: null, endDate: null }
    }
    const paidThroughDate = new Date(Date.parse(paidThrough.value))
    let startDate = new Date(new Date(paidThroughDate).setHours(paidThroughDate.getHours() - 1))
    let endDate = new Date(new Date(startDate).setMonth(startDate.getMonth() + 1))
    if (clientIsPrepay.value) {
      startDate = new Date(Date.now())
      endDate = new Date(new Date(startDate).setMonth(startDate.getMonth() + 1))
    }
    return {
      startDate: startDate.toLocaleDateString('en-US'),
      endDate: endDate.toLocaleDateString('en-US')
    }
  })
  const { buildContentMap } = useCancelReason()
  const boxContentToReasonMap = computed(() => {
    return buildContentMap.value(cancelReasons.value)
  })

  const cancelRequestDate = computed(() => {
    const requestDate = membershipDetails.value.cancelRequestDate 
      ? new Date(membershipDetails.value.cancelRequestDate) 
      : null
    return requestDate ? requestDate.toLocaleDateString('en-US') : null
  })

  const cancelEndDate = computed(() => {
    const endDate = membershipDetails.value.scheduledCancelDate 
      ? new Date(membershipDetails.value.scheduledCancelDate) 
      : null
    return endDate ? endDate.toLocaleDateString('en-US') : null
  })

  // Pause extension logic
  const clientIsPaused = computed(() => {
    return membershipStatus.value === 'paused'
  })

  const eligibleForPause = computed(() => {
    return membershipDetails.value?.selfServicePauseEligible
  })

  const ineligibleForPauseReason = computed(() => {
    return membershipDetails.value?.selfServicePauseIneligibleReason
  })

  const pauseResumeDate = computed(() => {
    return membershipDetails.value?.pauseResumeDate
  })

  const formattedPauseResumeDate = computed(() => {
    if (!pauseResumeDate.value) return ''
    return toLocaleDateString(new Date(pauseResumeDate.value))
  })

  const extensionEligibleDate = computed(() => {
    if (pauseResumeDate.value) {
      const eligibleDate = new Date(Date.parse(pauseResumeDate.value))
      eligibleDate.setDate(eligibleDate.getDate() - 7)
      eligibleDate.setHours(0, 0, 0, 0)
      return eligibleDate
    }
    return null
  })

  const formattedExtensionEligibleDate = computed(() => {
    if (!extensionEligibleDate.value) return ''
    return toLocaleDateString(extensionEligibleDate.value)
  })

  const canExtendPause = computed(() => {
    return extensionEligibleDate.value !== null && Date.now() > extensionEligibleDate.value
  })

  return {
    availablePerks,
    getBilling,
    getCancelReasons,
    cancelReasons,
    getItemPromotions,
    benefitsText,
    pauseCancelText,
    pauseCancelFaqs,
    pauseDates,
    boxContentToReasonMap,
    cancelRequestDate,
    cancelEndDate,
    clientIsPaused,
    eligibleForPause,
    ineligibleForPauseReason,
    pauseResumeDate,
    formattedPauseResumeDate,
    extensionEligibleDate,
    formattedExtensionEligibleDate,
    canExtendPause
  }
}
